import { useState, useRef, useEffect } from "react";
import { useAdminAuth } from "../contexts/admin-auth-context";
import { useTurnstile } from "../hooks/use-turnstile";
import { Loader2, ShieldCheck, ArrowRight, Moon, Sun } from "lucide-react";

function cn(...classes: (string | boolean | undefined | null)[]) {
  return classes.filter(Boolean).join(" ");
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

function ThemeToggle() {
  const [isDark, setIsDark] = useState(
    () => document.documentElement.classList.contains("dark")
  );
  function toggle() {
    const next = !isDark;
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
    setIsDark(next);
  }
  return (
    <button
      onClick={toggle}
      role="switch"
      aria-checked={isDark}
      aria-label={isDark ? "Açık temaya geç" : "Koyu temaya geç"}
      className={cn(
        "relative h-6 w-11 rounded-full border-2 transition-all duration-300 cursor-pointer shrink-0",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
        "hover:opacity-80",
        isDark ? "bg-primary/30 border-primary/50" : "bg-amber-200 border-amber-300"
      )}
    >
      <span
        className={cn(
          "absolute top-0.5 h-4 w-4 rounded-full shadow-sm transition-transform duration-300",
          "flex items-center justify-center",
          isDark ? "translate-x-[18px] bg-primary" : "translate-x-0.5 bg-amber-400"
        )}
      >
        {isDark
          ? <Moon className="h-2.5 w-2.5 text-white" />
          : <Sun className="h-2.5 w-2.5 text-white" />}
      </span>
    </button>
  );
}

export function AdminLoginPage() {
  const { requestCode, verifyCode } = useAdminAuth();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [digits, setDigits] = useState<string[]>(Array(6).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { containerRef: turnstileRef, token: turnstileToken, error: turnstileError, reset: resetTurnstile } = useTurnstile();
  const digitRefs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));
  const otpCode = digits.join("");

  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!turnstileToken) {
      setError("Lütfen robot olmadığınızı doğrulayın");
      return;
    }
    setLoading(true);
    try {
      await requestCode(email.trim().toLowerCase(), turnstileToken);
      resetTurnstile();
      setStep("code");
      setTimeout(() => digitRefs.current[0]?.focus(), 50);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Bir hata oluştu");
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyCode(email.trim().toLowerCase(), otpCode);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Bir hata oluştu");
    } finally {
      setLoading(false);
    }
  }

  function handleDigitChange(index: number, value: string) {
    const clean = value.replace(/\D/g, "");
    if (!clean) return;
    const next = [...digits];
    next[index] = clean[clean.length - 1];
    setDigits(next);
    if (index < 5) digitRefs.current[index + 1]?.focus();
  }

  function handleDigitKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const next = [...digits];
        next[index] = "";
        setDigits(next);
        if (index > 0) digitRefs.current[index - 1]?.focus();
      } else if (index > 0) {
        digitRefs.current[index - 1]?.focus();
      }
    }
  }

  function handleDigitPaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const next = Array(6).fill("");
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    digitRefs.current[Math.min(pasted.length, 5)]?.focus();
  }

  function goBack() {
    setStep("email");
    setDigits(Array(6).fill(""));
    setError("");
  }

  // ── Shared background ──────────────────────────────────────────
  const background = (
    <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
      <div className="absolute inset-0 bg-[#f7f7f7] dark:bg-[#1b1817]" />
      <div className="absolute inset-0" style={{ filter: "blur(80px)" }}>
        <div className="absolute top-[-15%] left-[5%] w-[65%] h-[65%] rounded-full bg-primary/15 dark:bg-primary/30" />
        <div className="absolute bottom-[-15%] right-[5%] w-[55%] h-[55%] rounded-full bg-primary/10 dark:bg-primary/20" />
        <div className="absolute top-[25%] right-[15%] w-[40%] h-[45%] rounded-full bg-primary/12 dark:bg-[rgba(180,130,95,0.22)]" />
      </div>
      <div className="absolute inset-0 bg-[#f7f7f7]/30 dark:bg-[#1b1817]/40" />
      <div
        className="absolute inset-0 opacity-10 dark:opacity-25"
        style={{
          backgroundImage: "radial-gradient(circle at 2px 2px, rgba(141,107,94,0.15) 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      />
    </div>
  );

  // ── Brand hero ─────────────────────────────────────────────────
  const hero = (
    <div className="text-center space-y-2">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 mb-1">
        <ShieldCheck className="w-7 h-7 text-primary" />
      </div>
      <h1 className="font-serif italic text-3xl text-primary leading-tight">Vite Gallery</h1>
      <p className="text-xs text-foreground/40 dark:text-white/40 uppercase tracking-widest">
        Platform Yönetimi
      </p>
    </div>
  );

  // ── Email step ─────────────────────────────────────────────────
  const emailStep = (
    <form onSubmit={handleRequestCode} className="space-y-5" noValidate>
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-0.5">Admin Girişi</h2>
        <p className="text-sm text-muted-foreground">Platform yönetici e-postanızı girin</p>
      </div>
      <div className="space-y-1.5">
        <label
          htmlFor="admin-email"
          className="block text-xs font-medium text-muted-foreground uppercase tracking-wider"
        >
          E-posta
        </label>
        <input
          id="admin-email"
          type="email"
          autoComplete="email"
          autoFocus
          required
          placeholder="admin@ornek.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-muted/40 border border-primary/20 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-lg py-3.5 px-4 text-base text-foreground placeholder:text-muted-foreground/50 transition-[border-color,box-shadow]"
        />
      </div>
      <div ref={turnstileRef} className="flex justify-center" />
      {(error || turnstileError) && (
        <p role="alert" className="text-sm text-red-500 dark:text-red-400">
          {error || turnstileError}
        </p>
      )}
      <button
        type="submit"
        disabled={loading || !email.trim() || !turnstileToken}
        className="w-full bg-primary hover:bg-primary/90 active:scale-[0.98] motion-reduce:active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-lg transition-[background-color,opacity,transform] shadow-lg shadow-primary/20 flex items-center justify-center gap-2 cursor-pointer"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        Kod Gönder
      </button>
    </form>
  );

  // ── OTP step ───────────────────────────────────────────────────
  const otpStep = (
    <form onSubmit={handleVerifyCode} className="space-y-5" noValidate>
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-0.5">Doğrulama Kodu</h2>
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{email}</span> adresine kod gönderildi
        </p>
      </div>
      <div
        className="grid grid-cols-6 gap-2"
        role="group"
        aria-label="Doğrulama kodu"
        onPaste={handleDigitPaste}
      >
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => { digitRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength={1}
            autoComplete={i === 0 ? "one-time-code" : "off"}
            value={digit}
            onChange={(e) => handleDigitChange(i, e.target.value)}
            onKeyDown={(e) => handleDigitKeyDown(i, e)}
            aria-label={`Doğrulama kodu ${i + 1}`}
            className="w-full h-14 bg-muted/40 border border-primary/20 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-lg text-center text-2xl font-bold text-foreground transition-[border-color,box-shadow]"
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Kod 5 dakika içinde geçerliliğini yitirir</p>
      {error && (
        <p role="alert" className="text-sm text-red-500 dark:text-red-400">{error}</p>
      )}
      <button
        type="submit"
        disabled={loading || otpCode.length < 6}
        className="w-full bg-primary hover:bg-primary/90 active:scale-[0.98] motion-reduce:active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-lg transition-[background-color,opacity,transform] shadow-lg shadow-primary/20 flex items-center justify-center gap-2 cursor-pointer"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
        {loading ? "Doğrulanıyor…" : "Giriş Yap"}
      </button>
      <button
        type="button"
        onClick={goBack}
        className="w-full text-sm text-muted-foreground hover:text-primary transition-colors py-2 cursor-pointer"
      >
        ← Farklı e-posta kullan
      </button>
    </form>
  );

  const formContent = step === "email" ? emailStep : otpStep;

  // ── MOBILE LAYOUT ──────────────────────────────────────────────
  if (!isDesktop) {
    return (
      <div className="relative min-h-screen overflow-hidden">
        {background}
        <div className="absolute top-4 right-4 z-20">
          <ThemeToggle />
        </div>
        <main className="relative z-10 flex flex-col min-h-screen max-w-sm mx-auto w-full px-6 pt-14 pb-8">
          {hero}
          <div className="bg-background/80 backdrop-blur-xl border border-primary/20 rounded-2xl p-7 mt-8 shadow-xl shadow-primary/5">
            {formContent}
          </div>
          <div className="flex-1" />
          <footer className="text-center mt-6">
            <p className="text-foreground/25 dark:text-white/20 text-[10px] uppercase tracking-[0.2em]">
              Cloudflare Access ile korunur
            </p>
          </footer>
        </main>
      </div>
    );
  }

  // ── DESKTOP LAYOUT ─────────────────────────────────────────────
  return (
    <div className="relative min-h-screen overflow-hidden">
      {background}
      <div className="absolute top-5 right-6 z-20">
        <ThemeToggle />
      </div>
      <main className="relative z-10 min-h-screen flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[900px] bg-background/80 backdrop-blur-xl border border-primary/20 rounded-2xl overflow-hidden shadow-2xl shadow-primary/10 flex">
          {/* Left decorative panel */}
          <div className="w-5/12 relative min-h-[500px] bg-[#efe8e4] dark:bg-[#262220]">
            <div style={{ filter: "blur(60px)" }} className="absolute inset-0 overflow-hidden">
              <div className="absolute top-[-20%] left-[-20%] w-[80%] h-[80%] rounded-full bg-primary/25 dark:bg-primary/40" />
              <div className="absolute bottom-[-20%] right-[-20%] w-[70%] h-[70%] rounded-full bg-primary/15 dark:bg-primary/25" />
              <div className="absolute top-[30%] right-[-10%] w-[50%] h-[50%] rounded-full bg-primary/20 dark:bg-primary/30" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 dark:from-[#1b1817]/60 to-transparent" />
            <div className="absolute inset-0 flex flex-col items-center justify-center p-10 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/20 dark:bg-white/5 border border-white/30 dark:border-primary/20 backdrop-blur-sm mb-5">
                <ShieldCheck className="w-10 h-10 text-white dark:text-primary" />
              </div>
              <h2 className="font-serif italic text-3xl text-white dark:text-foreground mb-2">
                Vite Gallery
              </h2>
              <p className="text-white/60 dark:text-muted-foreground text-sm">
                Platform Yönetim Paneli
              </p>
            </div>
            <div className="absolute bottom-6 left-0 right-0 text-center">
              <p className="text-white/30 dark:text-foreground/20 text-[10px] uppercase tracking-widest">
                Cloudflare Access ile korunur
              </p>
            </div>
          </div>

          {/* Right form panel */}
          <div className="flex-1 p-10 flex flex-col justify-center">
            {formContent}
          </div>
        </div>
      </main>
    </div>
  );
}
